import React, { useState } from 'react';
import { phoneScreeningsAPI } from '../../utils/api';
import { Drawer } from '../shared/DrawerManager';
import toastr from 'toastr';

const PhoneScreeningScheduler = ({ application, onClose, onScheduled }) => {
  const [submitting, setSubmitting] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [type, setType] = useState('phone');

  const handleSchedule = async () => {
    if (!scheduledDate || !scheduledTime) {
      toastr.error('Please select both date and time');
      return;
    }

    const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    if (dateTime < new Date()) {
      toastr.error('Please select a future date and time');
      return;
    }

    setSubmitting(true);
    try {
      await phoneScreeningsAPI.schedule({
        jobApplicationId: application._id,
        scheduledDate: dateTime.toISOString(),
        duration,
        type
      });

      toastr.success('Phone screening scheduled successfully!');
      if (onScheduled) {
        onScheduled();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to schedule phone screening:', error);
      if (error.response?.status === 409) {
        toastr.warning(error.response.data.error || 'A phone screening is already scheduled for this candidate');
      } else {
        toastr.error(error.response?.data?.error || 'Failed to schedule phone screening');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Quick time suggestions (today and tomorrow)
  const getQuickTimeSlots = () => {
    const slots = [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Today slots (if before 4 PM)
    if (now.getHours() < 16) {
      [14, 15, 16, 17].forEach(hour => {
        if (hour > now.getHours() || (hour === now.getHours() && now.getMinutes() < 30)) {
          const slotDate = today;
          const slotTime = `${hour.toString().padStart(2, '0')}:00`;
          slots.push({
            label: `Today at ${hour}:00 PM`,
            date: slotDate.toISOString().split('T')[0],
            time: slotTime
          });
        }
      });
    }

    // Tomorrow slots
    [10, 11, 14, 15, 16].forEach(hour => {
      slots.push({
        label: `Tomorrow at ${hour === 10 || hour === 11 ? hour + ':00 AM' : (hour === 12 ? '12:00 PM' : (hour - 12) + ':00 PM')}`,
        date: tomorrow.toISOString().split('T')[0],
        time: `${hour.toString().padStart(2, '0')}:00`
      });
    });

    return slots;
  };

  const quickSlots = getQuickTimeSlots();

  return (
    <Drawer
      isOpen={true}
      onClose={onClose}
      title="Schedule Phone Screening"
      width="md"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="drawer-btn drawer-btn-primary"
            onClick={handleSchedule}
            disabled={submitting}
          >
            {submitting ? 'Scheduling...' : 'Schedule Screening'}
          </button>
        </>
      }
    >
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Schedule a brief {duration}-minute {type === 'phone' ? 'phone' : 'video'} screening call with{' '}
          <strong>{application.candidateInfo.firstName} {application.candidateInfo.lastName}</strong>
        </p>
      </div>

      {/* Duration Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Duration
        </label>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <label style={{
            flex: 1,
            padding: '0.75rem',
            border: duration === 15 ? '2px solid #1E40AF' : '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            background: duration === 15 ? '#EFF6FF' : '#FFFFFF',
            textAlign: 'center',
            fontWeight: duration === 15 ? '600' : '400',
            color: duration === 15 ? '#1E40AF' : '#374151'
          }}>
            <input
              type="radio"
              name="duration"
              value="15"
              checked={duration === 15}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              style={{ display: 'none' }}
            />
            15 minutes
          </label>
          <label style={{
            flex: 1,
            padding: '0.75rem',
            border: duration === 30 ? '2px solid #1E40AF' : '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            background: duration === 30 ? '#EFF6FF' : '#FFFFFF',
            textAlign: 'center',
            fontWeight: duration === 30 ? '600' : '400',
            color: duration === 30 ? '#1E40AF' : '#374151'
          }}>
            <input
              type="radio"
              name="duration"
              value="30"
              checked={duration === 30}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              style={{ display: 'none' }}
            />
            30 minutes
          </label>
        </div>
      </div>

      {/* Type Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Call Type
        </label>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <label style={{
            flex: 1,
            padding: '0.75rem',
            border: type === 'phone' ? '2px solid #1E40AF' : '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            background: type === 'phone' ? '#EFF6FF' : '#FFFFFF',
            textAlign: 'center',
            fontWeight: type === 'phone' ? '600' : '400',
            color: type === 'phone' ? '#1E40AF' : '#374151'
          }}>
            <input
              type="radio"
              name="type"
              value="phone"
              checked={type === 'phone'}
              onChange={(e) => setType(e.target.value)}
              style={{ display: 'none' }}
            />
            Phone Call
          </label>
          <label style={{
            flex: 1,
            padding: '0.75rem',
            border: type === 'video' ? '2px solid #1E40AF' : '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            background: type === 'video' ? '#EFF6FF' : '#FFFFFF',
            textAlign: 'center',
            fontWeight: type === 'video' ? '600' : '400',
            color: type === 'video' ? '#1E40AF' : '#374151'
          }}>
            <input
              type="radio"
              name="type"
              value="video"
              checked={type === 'video'}
              onChange={(e) => setType(e.target.value)}
              style={{ display: 'none' }}
            />
            Quick Video
          </label>
        </div>
      </div>

      {/* Quick Time Selection */}
      {quickSlots.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Quick Select (Your Availability)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
            {quickSlots.map((slot, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setScheduledDate(slot.date);
                  setScheduledTime(slot.time);
                }}
                style={{
                  padding: '0.625rem 0.875rem',
                  border: scheduledDate === slot.date && scheduledTime === slot.time ? '2px solid #1E40AF' : '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  background: scheduledDate === slot.date && scheduledTime === slot.time ? '#EFF6FF' : '#FFFFFF',
                  fontSize: '0.875rem',
                  color: scheduledDate === slot.date && scheduledTime === slot.time ? '#1E40AF' : '#374151',
                  fontWeight: scheduledDate === slot.date && scheduledTime === slot.time ? '600' : '400'
                }}
                onMouseEnter={(e) => {
                  if (!(scheduledDate === slot.date && scheduledTime === slot.time)) {
                    e.target.style.borderColor = '#93C5FD';
                    e.target.style.background = '#F9FAFB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(scheduledDate === slot.date && scheduledTime === slot.time)) {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.background = '#FFFFFF';
                  }
                }}
              >
                {slot.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Date/Time */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Custom Date & Time
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            style={{
              padding: '0.625rem 0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
          <input
            type="time"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            style={{
              padding: '0.625rem 0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      {/* Screening Checklist Preview */}
      <div style={{
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
        padding: '1rem',
        marginTop: '1.5rem'
      }}>
        <div style={{ fontWeight: '500', marginBottom: '0.75rem', color: '#374151' }}>
          Screening Checklist (to be completed during call):
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#6B7280', fontSize: '0.875rem' }}>
          <li>Verify experience claims</li>
          <li>Discuss salary expectations</li>
          <li>Confirm availability to start</li>
          <li>Assess communication skills</li>
          <li>Answer candidate questions</li>
        </ul>
      </div>
    </Drawer>
  );
};

export default PhoneScreeningScheduler;


